import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  createTheme,
  ThemeProvider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { 
  ArrowBack,
  Brightness7 as Brightness7Icon,
  Brightness4 as Brightness4Icon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { useParams } from 'react-router-dom';

import MetaTags from '../components/MetaTags';
import SuccessDialog from '../components/SuccessDialog';
import { useSharedTheme } from '../theme/useSharedTheme';
import { getApiUrl } from '../utils/apiUrl';

const validationSchema = yup.object({
  emailOrUsername: yup
    .string()
    .required('Email or username is required'),
});

const ClientPasswordReset: React.FC = () => {
  const { companySlug } = useParams<{ companySlug: string }>();
  const { isDarkMode, toggleTheme, theme } = useSharedTheme();
  
  const [showCompanySelection, setShowCompanySelection] = useState(false);
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [companyDialogMessage, setCompanyDialogMessage] = useState('');
  
  // Success Dialog state
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: '',
    message: '',
    autoCloseDelay: 3000,
  });

  const formik = useFormik({
    initialValues: {
      emailOrUsername: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const response = await axios.post(`${getApiUrl()}/api/password-reset/request`, {
          emailOrUsername: values.emailOrUsername,
          accountType: 'client'
        });
        
        if (response.data.success) {
          if (response.data.requiresCompanySelection) {
            // Show company selection UI
            setCompanies(response.data.companies);
            setShowCompanySelection(true);
            setCompanyDialogMessage(response.data.message);
            setShowCompanyDialog(true);
          } else {
            setSuccessDialogConfig({
              title: 'Reset Link Sent',
              message: response.data.message || 'Password reset link sent to your email!',
              autoCloseDelay: 5000,
            });
            setShowSuccessDialog(true);
          }
        }
      } catch (error: any) {
        console.error('Password reset error:', error);
        setSuccessDialogConfig({
          title: 'Reset Failed',
          message: error.response?.data?.message || 'Failed to send reset link. Please try again.',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleCompanySelection = async (company: any) => {
    try {
      setIsSubmitting(true);
      setSelectedCompany(company);
      
      const response = await axios.post(`${getApiUrl()}/api/password-reset/request-company`, {
        clientId: company.clientId,
        accountType: 'client'
      });
      
      if (response.data.success) {
        setSuccessDialogConfig({
          title: 'Reset Link Sent',
          message: response.data.message,
          autoCloseDelay: 5000,
        });
        setShowSuccessDialog(true);
        setShowCompanySelection(false);
        setCompanies([]);
        setSelectedCompany(null);
        formik.resetForm();
      }
    } catch (error: any) {
      console.error('Company password reset error:', error);
      setSuccessDialogConfig({
        title: 'Reset Failed',
        message: error.response?.data?.message || 'Failed to send reset link. Please try again.',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get the last login URL or detect company context
  const getBackToLoginUrl = () => {
    // First, try to get the last login URL from sessionStorage
    const lastLoginUrl = sessionStorage.getItem('lastLoginUrl');
    if (lastLoginUrl) {
      return lastLoginUrl;
    }
    
    // Try to get company slug from sessionStorage first, then localStorage
    const savedCompanySlug = sessionStorage.getItem('companySlug') || localStorage.getItem('companySlug');
    if (savedCompanySlug) {
      // Clean up localStorage after use
      localStorage.removeItem('companySlug');
      return `/${savedCompanySlug}/login`;
    }
    
    // If no saved company slug, try to detect company context from current URL
    if (companySlug) {
      return `/${companySlug}/login`;
    }
    
    // Fallback to admin login
    return '/admin/login';
  };

  return (
    <ThemeProvider theme={theme}>
      <MetaTags 
        title="Client Password Reset - LeadForge"
        description="Reset your client password. Enter your email to receive a password reset link."
        keywords="client password reset, LeadForge, password recovery, client account"
      />
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          height: { xs: 'auto', md: '100vh' },
          overflow: { xs: 'auto', md: 'hidden' },
          background: { 
            xs: 'transparent', 
            md: isDarkMode 
              ? theme.palette.gradients.authDark
              : theme.palette.gradients.authLight
          },
          transition: 'background 0.5s ease-in-out',
          position: 'relative',
          fontFamily: '"Nunito Sans", sans-serif',
        }}
      >
        {/* Centered Form Container */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'flex-start' },
            justifyContent: { xs: 'flex-start', md: 'center' },
            width: '100%',
            minHeight: '100vh',
            height: { xs: 'auto', md: '100vh' },
            padding: { xs: 0, md: 4 },
            paddingTop: { xs: 0, md: 4 },
          }}
        >
          <Box 
            sx={{
              width: { xs: '100%', md: '60%' }, 
              maxWidth: { xs: 'none', md: 600 }, 
              backgroundColor: isDarkMode ? '#2e2e30' : '#ffffff',
              border: { xs: 'none', md: isDarkMode ? '0.3% solid #B9B9B9' : '0.3px solid #B9B9B9' },
              borderRadius: { xs: 0, md: 4 },
              padding: { xs: '10px', md: 0 },
              marginBottom: { xs: 0, md: 0 },
              boxShadow: { xs: 'none', md: '0 20px 40px rgba(0, 0, 0, 0.1)' },
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: { xs: '100vh', md: '600px' },
              transition: 'background-color 0.3s ease'
            }}
          >
            {/* Back Button - Top Left Corner (Mobile Only) */}
            <Box
              sx={{
                position: 'absolute',
                top: '2%',
                left: '4%',
                display: { xs: 'none', md: 'none' },
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                border: isDarkMode ? '1px solid #ffffff' : '1px solid #000000',
                borderRadius: 20,
                padding: 1.5,
                width: 40,
                height: 40,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                zIndex: 1000,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
              onClick={() => window.location.href = getBackToLoginUrl()}
            >
              <ArrowBack
                sx={{
                  color: isDarkMode ? '#ffffff' : '#1B4672',
                  fontSize: '20px',
                  transition: 'color 0.3s ease',
                }}
              />
            </Box>

            {/* Theme Toggle - Top Right Corner of Container */}
            <Box
              sx={{
                position: 'absolute',
                top: '1%',
                right: '1%',
                display: { xs: 'none', md: 'none' },
                alignItems: 'center',
                backgroundColor: 'transparent',
                border: isDarkMode ? '1px solid #ffffff' : '1px solid #000000',
                borderRadius: 20,
                padding: { xs: 1.5, md: 2 },
                width: { xs: 60, md: 80 },
                height: { xs: 32, md: 40 },
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                zIndex: 1000,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
              onClick={toggleTheme}
            >
              {/* Sun Icon - Left Side */}
              <Box
                sx={{
                  position: 'absolute',
                  left: { xs: 3, md: 4 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 20, md: 24 },
                  height: { xs: 20, md: 24 },
                  zIndex: 2,
                }}
              >
                <Brightness7Icon 
                  sx={{ 
                    fontSize: { xs: 16, md: 20 },
                    color: isDarkMode ? '#ffffff' : '#000000',
                    transition: 'all 0.3s ease',
                    opacity: isDarkMode ? 0.3 : 1,
                  }} 
                />
              </Box>
              
              {/* Moon Icon - Right Side */}
              <Box
                sx={{
                  position: 'absolute',
                  right: { xs: 3, md: 4 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 20, md: 24 },
                  height: { xs: 20, md: 24 },
                  zIndex: 2,
                }}
              >
                <Brightness4Icon 
                  sx={{ 
                    fontSize: { xs: 16, md: 20 },
                    color: isDarkMode ? '#ffffff' : '#000000',
                    transition: 'all 0.3s ease',
                    opacity: isDarkMode ? 1 : 0.3,
                  }} 
                />
              </Box>
              
              {/* Toggle Switch */}
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: 2, md: 4 },
                  left: isDarkMode ? { xs: 2, md: 4 } : { xs: 30, md: 44 },
                  width: { xs: 28, md: 32 },
                  height: { xs: 28, md: 32 },
                  background: isDarkMode ? '#ffffff' : '#1B4672',
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  transition: 'left 0.3s ease, background 0.3s ease',
                  zIndex: 3,
                }}
              />
            </Box>

            {/* Second Container - Center: Form Content */}
            <Box sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: { xs: 'flex-start', md: 'center' },
              alignItems: 'center',
              flex: 1,
              padding: { xs: '10px', md: '20px' },
            }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: isDarkMode ? '#ffffff' : '#202224',
                  mb: 2,
                  textAlign: 'center',
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  letterSpacing: '-0.11px',
                }}
              >
                Client Password Reset
              </Typography>

              {/* Description */}
              <Typography
                variant="body1"
                sx={{
                  color: isDarkMode ? '#ffffff' : '#666666',
                  mb: 4,
                  textAlign: 'center',
                  fontSize: { xs: '0.9rem', md: '16px' },
                  letterSpacing: '-0.06px',
                  opacity: 0.8,
                  maxWidth: '350px',
                  lineHeight: 1.4,
                }}
              >
                Enter your client email or username to<br />receive a password reset link.
              </Typography>

              {/* Form */}
              <Box sx={{ width: '100%', maxWidth: '400px' }}>
              <form onSubmit={formik.handleSubmit}>
                {/* Email Field */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      color: isDarkMode ? '#ffffff' : '#202224',
                      mb: 1,
                      fontSize: '0.95rem',
                    }}
                  >
                    Email Address:
                  </Typography>
                  <TextField
                    fullWidth
                    id="emailOrUsername"
                    name="emailOrUsername"
                    placeholder="client@company.com or username"
                    value={formik.values.emailOrUsername}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.emailOrUsername && Boolean(formik.errors.emailOrUsername)}
                    helperText={formik.touched.emailOrUsername && formik.errors.emailOrUsername}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '30px',
                        backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                        border: `${isDarkMode ? 'none' : '1px solid #e2e8f0'}`,
                        '& fieldset': {
                          border: 'none',
                          display: 'none',
                        },
                        '& input': {
                          color: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                          fontSize: '1rem',
                          padding: '12px 16px',
                          backgroundColor: 'transparent',
                        },
                        '& input:-webkit-autofill': {
                          backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                          WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#f8fafc'} inset`,
                          WebkitTextFillColor: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                        },
                        '& input:-webkit-autofill:hover': {
                          backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                          WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#f1f5f9'} inset`,
                        },
                        '& input:-webkit-autofill:focus': {
                          backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                          WebkitBoxShadow: `0 0 0 1000px ${isDarkMode ? '#2e2e30' : '#ffffff'} inset`,
                        },
                        '& input::placeholder': {
                          color: `${isDarkMode ? '#A6A6A6' : '#999999'}`,
                          opacity: '1',
                        },
                        '&:hover': {
                          backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                          border: `${isDarkMode ? 'none' : '1px solid #cbd5e1'}`,
                          '& fieldset': {
                            border: 'none',
                            display: 'none',
                          },
                        },
                        '&.Mui-focused': {
                          backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                          border: '1px solid #6052A2',
                          boxShadow: '0 0 0 3px rgba(96, 82, 162, 0.1)',
                          '& fieldset': {
                            border: 'none',
                            display: 'none',
                          },
                        },
                      },
                    }}
                  />
                </Box>

                {/* Send Reset Link Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                      py: 1.5,
                      px: 6,
                      borderRadius: 2,
                      background: isDarkMode 
                        ? '#ffffff' 
                        : '#1B4672',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: isDarkMode ? '#1B4672' : 'white',
                      '&:hover': {
                        background: isDarkMode 
                          ? '#f0f0f0' 
                          : '#1B4672',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </Box>
              </form>

              {/* Back to Client Login */}
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  mt: 2,
                  color: isDarkMode ? '#ffffff' : '#666666',
                }}
              >
                <Link
                  href={`/${companySlug}/login`}
                  sx={{
                    color: isDarkMode ? '#ffffff' : '#1B4672',
                    textDecoration: 'underline',
                    fontWeight: 500,
                    fontSize: '16px',
                    '&:hover': {
                      color: isDarkMode ? '#ffffff' : '#1B4672',
                    },
                  }}
                >
                  Back to Client Login
                </Link>
              </Typography>
            </Box>

            {/* Company Selection UI */}
              {showCompanySelection && (
                <Box sx={{ width: '100%', maxWidth: '400px', mt: 3 }}>
                  <FormControl fullWidth size="medium">
                    <Select
                      id="company-select"
                      displayEmpty
                      value={selectedCompany?.clientId || ''}
                      onChange={(e) => {
                        const chosen = companies.find(c => c.clientId === e.target.value);
                        setSelectedCompany(chosen || null);
                      }}
                      sx={{
                        borderRadius: '30px',
                        backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                        border: `${isDarkMode ? 'none' : '1px solid #e2e8f0'}`,
                        '& fieldset': {
                          border: 'none',
                          display: 'none',
                        },
                        '& .MuiSelect-select': {
                          color: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                          fontSize: '1rem',
                          padding: '12px 16px',
                          backgroundColor: 'transparent',
                        },
                        '&:hover': {
                          backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                          border: `${isDarkMode ? 'none' : '1px solid #cbd5e1'}`,
                          '& fieldset': {
                            border: 'none',
                            display: 'none',
                          },
                        },
                        '&.Mui-focused': {
                          backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                          border: '1px solid #6052A2',
                          boxShadow: '0 0 0 3px rgba(96, 82, 162, 0.1)',
                          '& fieldset': {
                            border: 'none',
                            display: 'none',
                          },
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            backgroundColor: `${isDarkMode ? theme.palette.custom.background.modal : '#ffffff'}`,
                            border: `${isDarkMode ? 'none' : '1px solid #e2e8f0'}`,
                            borderRadius: '30px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            '& .MuiMenuItem-root': {
                              color: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                              backgroundColor: 'transparent',
                              '&:hover': {
                                backgroundColor: `${isDarkMode ? '#e2e8f0' : '#f1f5f9'}`,
                              },
                              '&.Mui-selected': {
                                backgroundColor: `${isDarkMode ? '#cbd5e1' : '#e2e8f0'}`,
                                '&:hover': {
                                  backgroundColor: `${isDarkMode ? '#cbd5e1' : '#e2e8f0'}`,
                                },
                              },
                            },
                          },
                        },
                      }}
                      renderValue={(value) => {
                        if (!value) {
                          return <span style={{ color: isDarkMode ? '#A6A6A6' : '#999999' }}>Choose company</span>;
                        }
                        const company = companies.find(c => c.clientId === value);
                        return company ? company.companyName : '';
                      }}
                    >
                      {companies.map((company) => (
                        <MenuItem key={company.clientId} value={company.clientId}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography sx={{ fontWeight: 600 }}>{company.companyName}</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.7, fontSize: '0.8rem' }}>{company.email}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowCompanySelection(false);
                        setCompanies([]);
                        setSelectedCompany(null);
                      }}
                      sx={{
                        color: isDarkMode ? '#ffffff' : '#1B4672',
                        borderColor: isDarkMode ? '#ffffff' : '#1B4672',
                        textTransform: 'none',
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!selectedCompany || isSubmitting}
                      onClick={() => selectedCompany && handleCompanySelection(selectedCompany)}
                      sx={{
                        textTransform: 'none',
                        background: isDarkMode ? '#ffffff' : '#1B4672',
                        color: isDarkMode ? '#1B4672' : 'white',
                        '&:hover': { background: isDarkMode ? '#f0f0f0' : '#1B4672' }
                      }}
                    >
                      {isSubmitting ? 'Sending...' : 'Confirm'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Third Container - Bottom: Account Link */}
            <Box sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: { xs: '10px', md: '20px' },
            }}>
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  color: isDarkMode ? '#ffffff' : '#666666',
                  fontSize: '0.85rem',
                }}
              >
                Don't have an account?{' '}
                <Link
                  href={`/${companySlug}/client-signup`}
                  sx={{
                    color: isDarkMode ? '#ffffff' : '#1B4672',
                    textDecoration: 'underline',
                    fontWeight: 500,
                    fontSize: '16px',
                    '&:hover': {
                      color: isDarkMode ? '#ffffff' : '#1B4672',
                    },
                  }}
                >
                  Request Access
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Company Selection Dialog */}
      <SuccessDialog
        open={showCompanyDialog}
        onClose={() => setShowCompanyDialog(false)}
        title="Multiple Accounts Found"
        message={companyDialogMessage}
        buttonText="Got it"
        autoCloseDelay={0}
      />
    </ThemeProvider>
  );
};

export default ClientPasswordReset;